/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */import { Box, IconButton, Step, StepLabel, Stepper } from '@mui/material';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { LayoutBase } from '../../../layouts';
import { ArrowCircleLeft, ArrowCircleRight, CheckCircle } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomAlert } from '../../../components/shared';
import { useAuthContext } from '../../../context';
import { AnaliseEstabilidade, Monitoramentos, RISR, Recomendacoes, ReportsServicesRisr, VerificacaoHidrologica } from '../../../services/api/reports/ReportsServicesRisr';
import { AnalysisRisr, GeneralRisr, HydrologicalCheckRisr, MonitoringRisr, RecommendationRisr, ToolsDamInformation } from '../../../components/custom';
import HorizontalStepperWithScroll from '../../../components/shared/StepsCarrocel';

const steps = ['GERAL', 'ANÁLISES', 'VERIFICAÇÃO', 'MONITORAMENTOS', 'RECOMENDAÇÕES',];

interface StepContentProps {
    step: number;
    onSubmit: (data: any) => void;
    setSubmitCallback: (callback: () => void) => void;
    data: RISR;
    handleAnalysis: (data: AnaliseEstabilidade) => void;
    handleHydrologicalChange: (data: VerificacaoHidrologica) => void;
    handleMonitoringChange: (data: Monitoramentos[]) => void;
    handleRecommendationChange: (data: Recomendacoes[]) => void;
}

const StepContent = ({ step, onSubmit, setSubmitCallback, data, handleAnalysis, handleHydrologicalChange, handleMonitoringChange, handleRecommendationChange }: StepContentProps) => {
    switch (step) {
        case 0:
            return <GeneralRisr onSubmit={onSubmit} setSubmitCallback={setSubmitCallback} data={data} />;
        case 1:
            return <AnalysisRisr dataRisr={data} onAnalysisChange={handleAnalysis} />;
        case 2:
            return <HydrologicalCheckRisr dataRisr={data} onDataChange={handleHydrologicalChange} />;
        case 3:
            return <MonitoringRisr dataRisr={data} onDataChange={handleMonitoringChange} />;
        case 4:
            return <RecommendationRisr dataRisr={data} onDataChange={handleRecommendationChange} />;
        default:
            return null;
    }
};

export const RegisterReportsRisr = () => {
    const [activeStep, setActiveStep] = useState(0);
    const submitCallbackRef = useRef<() => void>();

    const { userData } = useAuthContext();
    const permissions = userData.permissoes;

    const { risr } = useParams();
    const { id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState<RISR>({
        id_barragem: '',
        periodo_referencia: '',
        empresa_executora: '',
        data_inspecao: '',
        recomendacoes: [],
        monitoramentos: [],
        verificacaoHidrologica: {},
        analiseEstabilidade: { informacoes: [] },
        npgb: ''
    });

    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const pagePermissionsString: string | undefined = useMemo(() => {
        return userData?.nivel_acesso?.registros?.RISR;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view' | 'download'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    useEffect(() => {
        fetchData();
    }, [risr]);

    const fetchData = () => {
        if (risr === 'new') {
            const newData: RISR = {
                id_barragem: '',
                periodo_referencia: '',
                empresa_executora: '',
                data_inspecao: '',
                recomendacoes: [],
                monitoramentos: [],
                verificacaoHidrologica: {},
                analiseEstabilidade: { informacoes: [] },
                npgb: ''
            };
            setData(newData);
        } else {
            if (risr) {
                ReportsServicesRisr.get(risr)
                    .then(result => {
                        if (result instanceof Error) {
                            return result.message;
                        } else {
                            setData(result);
                        }
                    });

            }
        }
    };

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    const handleNext = () => {
        if (submitCallbackRef.current) {
            submitCallbackRef.current();
        } else {
            setActiveStep(prevActiveStep => prevActiveStep + 1);
        }
    };

    const handleFormSubmit = useCallback((formData: Partial<RISR>) => {
        setData(prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    ...formData,
                };
            }
            return prevData;
        });
        setActiveStep(prevActiveStep => prevActiveStep + 1);
    }, []);

    const handleAnalysisChange = useCallback((newAnalysisData: AnaliseEstabilidade) => {
        setData(prevData => {
            if (prevData && JSON.stringify(prevData.analiseEstabilidade) === JSON.stringify(newAnalysisData)) {
                return prevData;
            }
            return {
                ...prevData,
                analiseEstabilidade: newAnalysisData,
            };
        });
    }, []);

    const handleHydrologicalChange = useCallback((newHydrologicalData: VerificacaoHidrologica) => {
        setData(prevData => {
            if (prevData && JSON.stringify(prevData.verificacaoHidrologica) === JSON.stringify(newHydrologicalData)) {
                return prevData;
            }
            return {
                ...prevData,
                verificacaoHidrologica: newHydrologicalData,
            };
        });
    }, []);

    const handleMonitoringChange = useCallback((newMonitoring: Monitoramentos[]) => {
        setData(prevData => {
            if (prevData && JSON.stringify(prevData.monitoramentos) === JSON.stringify(newMonitoring)) {
                return prevData;
            }
            return {
                ...prevData,
                monitoramentos: newMonitoring,
            };
        });
    }, []);

    const handleRecommendationChange = useCallback((newRecommendation: Recomendacoes[]) => {
        setData(prevData => {
            if (prevData && JSON.stringify(prevData.recomendacoes) === JSON.stringify(newRecommendation)) {
                return prevData;
            }
            return {
                ...prevData,
                recomendacoes: newRecommendation,
            };
        });
    }, []);

    const setSubmitCallback = useCallback((callback: () => void) => {
        submitCallbackRef.current = callback;
    }, []);

    const handleSave = async () => {
        const isEditing = risr && risr !== 'new';
        const canAdd = hasActionPermission('add');
        const canEdit = hasActionPermission('edit');

        if ((!isEditing && !canAdd) || (isEditing && !canEdit)) {
            setAlertInfo({ open: true, message: 'Verifique seu nível de acesso!', severity: 'warning' });
            return;
        }

        if (data !== undefined && id !== undefined) {
            const dataToApi = data;
            dataToApi.id_barragem = id;

            if (risr === 'new') {
                const response = await ReportsServicesRisr.create(dataToApi);
                if (response instanceof Error) {
                    setAlertInfo({ open: true, message: 'Erro ao salvar relatório!', severity: 'error' });
                } else {
                    setAlertInfo({ open: true, message: 'Relatório salvo!', severity: 'success' });
                    setTimeout(() => {
                        navigate(`/information_dam/${id}/risr`);
                    }, 2000);
                }
            } else {
                if (risr !== undefined) {
                    const response = await ReportsServicesRisr.upgrade(dataToApi, risr);
                    if (response instanceof Error) {
                        setAlertInfo({ open: true, message: `Erro ao salvar relatório: ${response.message}`, severity: 'error' });
                    } else {
                        setAlertInfo({ open: true, message: 'Relatório salvo!', severity: 'success' });
                        setTimeout(() => {
                            navigate(`/information_dam/${id}/risr`);
                        }, 2000);
                    }
                }
            }
        }
    };

    const handleNextStep = () => {
        if (activeStep === steps.length - 1) {
            handleSave();
        } else {
            handleNext();
        }
    };

    return (
        <LayoutBase
            title='Relatórios de Inspeção de Segurança Regular (RISR)'
            tollBar={<ToolsDamInformation />}
        >
            <Box width={'100%'}>
                <HorizontalStepperWithScroll activeStep={activeStep} steps={steps} />
                <Box sx={{ marginTop: 2 }} width={'90%'} padding={1} marginLeft={1}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <IconButton
                            color="inherit"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                        >
                            <ArrowCircleLeft sx={{ height: 40, width: 40 }} />
                        </IconButton>

                        <Box sx={{ flex: '1 1 auto' }} />
                        {activeStep === steps.length - 1 ? (
                            <IconButton
                                onClick={handleNextStep}
                                disabled={
                                    (risr === 'new' && !hasActionPermission('add')) ||
                                    (risr !== 'new' && !hasActionPermission('edit'))
                                }
                            >
                                <CheckCircle sx={{ height: 40, width: 40 }} />
                            </IconButton>
                        ) : (
                            <IconButton onClick={handleNext}>
                                <ArrowCircleRight sx={{ height: 40, width: 40 }} />
                            </IconButton>
                        )}
                    </Box>
                    <StepContent
                        step={activeStep}
                        onSubmit={handleFormSubmit}
                        setSubmitCallback={setSubmitCallback}
                        data={data}
                        handleAnalysis={handleAnalysisChange}
                        handleHydrologicalChange={handleHydrologicalChange}
                        handleMonitoringChange={handleMonitoringChange}
                        handleRecommendationChange={handleRecommendationChange}
                    />
                </Box>
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
};
